/**
 * COMPONENTE DE RUTA PÚBLICA
 * 
 * Este componente maneja rutas públicas que no requieren autenticación.
 * Redirige a usuarios ya autenticados al dashboard correspondiente a su rol.
 * 
 * @author Diego Alejandro
 * @version 3.0
 */

import { useAuth } from '../../context/authContext';
import { Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import PropTypes from 'prop-types';

// Configuración centralizada de roles
import { getDashboardRoute } from '../../config/roleRoutes';

const PublicRoute = ({ 
  children, 
  redirectIfAuthenticated = true,
  customRedirectPath = null 
}) => {
  const { isAuthenticated, loading, user } = useAuth();

  // Mostrar loading mientras se verifica la autenticación
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Si está autenticado y se debe redirigir
  if (isAuthenticated && redirectIfAuthenticated) {
    // Si hay una ruta personalizada, usar esa
    if (customRedirectPath) {
      return <Navigate to={customRedirectPath} replace />;
    }
    
    // Si no, redirigir al dashboard correspondiente al rol
    const redirectRoute = getDashboardRoute(user?.role);
    return <Navigate to={redirectRoute} replace />;
  }

  // Renderizar el contenido (sea autenticado o no)
  return children;
};

PublicRoute.propTypes = {
  children: PropTypes.node.isRequired,
  redirectIfAuthenticated: PropTypes.bool,
  customRedirectPath: PropTypes.string,
};

PublicRoute.defaultProps = {
  redirectIfAuthenticated: true,
  customRedirectPath: null,
};

export default PublicRoute;